import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Button } from '@mui/material';

const containerStyle = {
    width: '100%',
    height: '300px',
};

const defaultCenter = {
    lat: 20.5937, // India center
    lng: 78.9629,
};

const LocationPicker = ({ latitude, longitude, setLatitude, setLongitude }) => {
    // State to track geolocation status
    const [geoError, setGeoError] = useState(null);

    const getMyLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setGeoError('Geolocation is not supported by your browser.');
            alert('Geolocation is not supported by your browser.');
            return;
        }

        setGeoError(null); // Clear previous errors
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                console.log('Fetched geolocation:', { lat, lng });

                if (!isNaN(lat) && !isNaN(lng)) {
                    setLatitude(lat);
                    setLongitude(lng);
                } else {
                    console.error('Invalid geolocation coordinates:', { lat, lng });
                    setGeoError('Invalid coordinates received.');
                }
            },
            (error) => {
                console.error('Geolocation error:', error.message);
                setGeoError(`Could not fetch location: ${error.message}`);
                alert(`Could not fetch your location: ${error.message}. Please allow location access or select a location on the map.`);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    }, [setLatitude, setLongitude]);

    const onMapClick = useCallback(
        (e) => {
            const lat = e.latLng?.lat();
            const lng = e.latLng?.lng();
            console.log('Map click coordinates:', { lat, lng });

            if (!isNaN(lat) && !isNaN(lng)) {
                setLatitude(lat);
                setLongitude(lng);
                setGeoError(null); // Clear any geolocation errors
            } else {
                console.error('Invalid map click coordinates:', { lat, lng });
            }
        },
        [setLatitude, setLongitude]
    );

    // Use Google Geolocation API as a fallback if navigator.geolocation fails
    const fetchGoogleGeolocation = useCallback(async () => {
        try {
            const response = await fetch(
                `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
                { method: 'POST' }
            );
            const data = await response.json();
            if (data.location) {
                const { lat, lng } = data.location;
                console.log('Google Geolocation API response:', { lat, lng });
                if (!isNaN(lat) && !isNaN(lng)) {
                    setLatitude(lat);
                    setLongitude(lng);
                    setGeoError(null);
                } else {
                    throw new Error('Invalid coordinates from Google Geolocation API');
                }
            } else {
                throw new Error('No location data in Google Geolocation API response');
            }
        } catch (err) {
            console.error('Google Geolocation API error:', err);
            setGeoError('Failed to fetch location from Google API.');
        }
    }, [setLatitude, setLongitude]);

    const center = useMemo(() => {
        const lat = Number(latitude);
        const lng = Number(longitude);
        return latitude && longitude && !isNaN(lat) && !isNaN(lng)
            ? { lat, lng }
            : defaultCenter;
    }, [latitude, longitude]);

    // Attempt geolocation on component mount
    useEffect(() => {
        getMyLocation();
    }, [getMyLocation]);

    return (
        <div>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude)) ? 15 : 5}
                onClick={onMapClick}
            >
                {latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude)) && (
                    <Marker position={{ lat: Number(latitude), lng: Number(longitude) }} />
                )}
            </GoogleMap>

            <Button
                variant="outlined"
                onClick={getMyLocation}
                style={{ marginTop: '10px' }}
            >
                Use My Location
            </Button>

            {/* Fallback button for Google Geolocation API */}
            {geoError && (
                <Button
                    variant="outlined"
                    onClick={fetchGoogleGeolocation}
                    style={{ marginTop: '10px', marginLeft: '10px' }}
                >
                    Try Google Geolocation
                </Button>
            )}

            {latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude)) && (
                <p style={{ marginTop: '10px' }}>
                    📍 Latitude: {Number(latitude).toFixed(5)}, Longitude: {Number(longitude).toFixed(5)}
                </p>
            )}

            {geoError && (
                <p style={{ marginTop: '10px', color: 'red' }}>
                    Error: {geoError}
                </p>
            )}
        </div>
    );
};

export default LocationPicker;
